import type { DocumentSectionTree } from '../../types/files'
import type {
  HeadingNumbering,
  PlainTextParagraph,
} from '../../utils/html-sanitization/inject-heading-numbers'
import { formatPlainTextParagraph } from '../../utils/html-sanitization/inject-heading-numbers'

// Yield to the event loop to keep the UI responsive during heavy document processing
export const yieldToMain = () => new Promise<void>((resolve) => setTimeout(resolve, 0))
export const PARAGRAPH_CHUNK_SIZE = 100

/**
 * Helper function to determine if a paragraph is a heading
 * Only trusts built-in heading styles (Heading1-Heading9)
 * Ignores outlineLevel to avoid false positives from styled text
 */
export function getHeadingLevel(paragraph: Word.Paragraph): number | null {
  const style = paragraph.styleBuiltIn

  // Only check for built-in heading styles
  if (
    style === 'Heading1' ||
    style === 'Heading2' ||
    style === 'Heading3' ||
    style === 'Heading4' ||
    style === 'Heading5' ||
    style === 'Heading6' ||
    style === 'Heading7' ||
    style === 'Heading8' ||
    style === 'Heading9'
  ) {
    return parseInt(style.replace('Heading', ''), 10)
  }

  return null
}

/**
 * Generic tree insertion — manages a stack to maintain proper nesting.
 * Works for any node type that carries a children array,
 * currently for `DocumentSectionTree` and `WorkingDocumentSection`.
 *
 * @param node - The new node to insert, e.g. a heading or table entry
 * @param level - Heading depth (1–9). Tables use parentLevel + 1 so they nest under their heading.
 *   Example: an H2 has level 2; a table under an H2 gets level 3.
 * @param stack - Tracks which headings are still "open" as we walk paragraphs top-to-bottom.
 *   The top of the stack is the current parent — the node that the next child attaches to.
 *   When a heading arrives that is at the same level or higher (less deep) than what's on top,
 *   those entries are popped because their section has ended.
 *
 *   Walking H1 → H2 → H3 → H2:
 *   - After H1:      stack = [{H1, 1}]               — H1 is the current parent
 *   - After H2:      stack = [{H1, 1}, {H2, 2}]      — H2 nested under H1
 *   - After H3:      stack = [{H1, 1}, {H2, 2}, {H3, 3}]
 *   - Next H2 seen:  H3 (level 3) and old H2 (level 2) are both popped because the new H2
 *                    is at level 2 — same or higher. New H2 attaches to H1.
 *                    stack = [{H1, 1}, {newH2, 2}]
 * @param roots - The top-level result array. Nodes with no parent are pushed here.
 *   Example: two H1 headings both end up in roots; their children do not.
 */
export function insertIntoTree<T extends { children: T[] }>(
  node: T,
  level: number,
  stack: { entry: T; level: number }[],
  roots: T[]
): void {
  while (stack.length > 0 && stack[stack.length - 1].level >= level) {
    stack.pop()
  }
  if (stack.length === 0) {
    roots.push(node)
  } else {
    stack[stack.length - 1].entry.children.push(node)
  }
  stack.push({ entry: node, level })
}

/**
 * Helper function to process a heading and add it to the hierarchical structure
 * Manages the stack to maintain proper nesting hierarchy
 */
export function processHeading(params: {
  heading: DocumentSectionTree
  headingLevel: number
  stack: { entry: DocumentSectionTree; level: number }[]
  rootEntries: DocumentSectionTree[]
}): void {
  const { heading, headingLevel, stack, rootEntries } = params
  insertIntoTree(heading, headingLevel, stack, rootEntries)
}

/**
 * Helper function to process a table and add it to the hierarchical structure
 * Avoids duplicate inserts by tracking the table's paragraphId, which is the
 * uniqueLocalId of the table's first paragraph — document-globally unique.
 */
export function processTable(params: {
  tableTitle: string
  paragraphId: string
  stack: { entry: DocumentSectionTree; level: number }[]
  rootEntries: DocumentSectionTree[]
  addedTableIds: Set<string>
}): void {
  const { tableTitle, paragraphId, stack, rootEntries, addedTableIds } = params

  if (!addedTableIds.has(paragraphId)) {
    addedTableIds.add(paragraphId)

    const tableEntry: DocumentSectionTree = {
      id: paragraphId,
      type: 'table',
      title: tableTitle,
      children: [],
    }

    if (stack.length === 0) {
      rootEntries.push(tableEntry)
    } else {
      const parent = stack[stack.length - 1].entry
      parent.children.push(tableEntry)
    }
  }
}

/**
 * Shared two-sync document loader used by both getDocumentHierarchicalStructure
 * and getWorkingDocumentSnapshot.
 *
 * Sync 1: lightweight paragraph load (text, style, id) + table collection.
 * Sync 2: listItemOrNullObject for heading paragraphs only
 *     + first-cell/paragraph-IDs for tables.
 *
 * Returns the loaded paragraph collection and a map of paragraphId → { id, title }
 * so callers can identify which table each paragraph belongs to without further syncs.
 * The table id is the uniqueLocalId of the table's first paragraph — stable across
 * the session and consistent with getSectionContentById / getTableByParagraphId.
 */
export async function loadDocumentData(context: Word.RequestContext): Promise<{
  paragraphs: Word.ParagraphCollection
  paragraphToTableInfo: Map<string, { id: string; title: string }>
}> {
  const paragraphs = context.document.body.paragraphs
  paragraphs.load('text, styleBuiltIn, uniqueLocalId')

  const tables = context.document.body.tables
  tables.load('items')

  await context.sync()
  await yieldToMain()

  for (const paragraph of paragraphs.items) {
    if (getHeadingLevel(paragraph) !== null) {
      paragraph.listItemOrNullObject.load('listString, isNullObject')
    }
  }

  type TableInfo = { firstCell: Word.TableCell; tableRangeParagraphs: Word.ParagraphCollection }
  const tableInfos: TableInfo[] = []

  for (const table of tables.items) {
    const firstCell = table.getCellOrNullObject(0, 0)
    firstCell.load('value, isNullObject')
    const tableRangeParagraphs = table.getRange('Whole').paragraphs
    tableRangeParagraphs.load('uniqueLocalId')
    tableInfos.push({ firstCell, tableRangeParagraphs })
  }

  await context.sync()
  await yieldToMain()

  const paragraphToTableInfo = new Map<string, { id: string; title: string }>()
  for (const { firstCell, tableRangeParagraphs } of tableInfos) {
    if (firstCell.isNullObject) continue
    const title = firstCell.value.trim()
    const tableId = tableRangeParagraphs.items[0]?.uniqueLocalId
    if (!tableId) continue
    for (const tp of tableRangeParagraphs.items) {
      paragraphToTableInfo.set(tp.uniqueLocalId, { id: tableId, title })
    }
  }

  return { paragraphs, paragraphToTableInfo }
}

/** Loads listItem.listString for built-in heading paragraphs (no-op when none are present). */
async function syncHeadingListItems(
  context: Word.RequestContext,
  paragraphs: Word.Paragraph[]
): Promise<void> {
  const headingParagraphs = paragraphs.filter((paragraph) => getHeadingLevel(paragraph) !== null)
  for (const paragraph of headingParagraphs) {
    paragraph.listItemOrNullObject.load('listString, isNullObject')
  }

  if (headingParagraphs.length > 0) {
    await context.sync()
  }
}

/** Maps a Word paragraph to plain text plus listString when it is a numbered built-in heading. */
function toPlainTextParagraph(paragraph: Word.Paragraph): PlainTextParagraph {
  if (getHeadingLevel(paragraph) === null) {
    return { text: paragraph.text }
  }

  const listItem = paragraph.listItemOrNullObject
  const sectionNumber =
    !listItem.isNullObject && listItem.listString?.trim() ? listItem.listString.trim() : undefined

  return sectionNumber ? { text: paragraph.text, sectionNumber } : { text: paragraph.text }
}

/** Loads paragraphs in a range plus heading listString, shared by the HTML and plain-text readers below. */
async function loadRangeParagraphs(
  context: Word.RequestContext,
  range: Word.Range,
  properties: string
): Promise<Word.Paragraph[]> {
  const paragraphs = range.paragraphs
  paragraphs.load(properties)
  await context.sync()
  await syncHeadingListItems(context, paragraphs.items)

  return paragraphs.items
}

/**
 * Collects list numbering for built-in heading paragraphs within a range.
 * Used to enrich getHtml() output where auto-numbers are not in paragraph text.
 * queueBeforeSync lets the caller queue another range read (e.g. getHtml()) into the first sync,
 * returning its result alongside the headings so callers never read it before it's populated.
 */
export async function collectHeadingNumbersInRange<T>(
  context: Word.RequestContext,
  range: Word.Range,
  queueBeforeSync: () => T
): Promise<{ headings: HeadingNumbering[]; queued: T }> {
  const queued = queueBeforeSync()
  const paragraphs = await loadRangeParagraphs(context, range, 'uniqueLocalId, text, styleBuiltIn')

  const headings: HeadingNumbering[] = []
  for (const paragraph of paragraphs) {
    if (getHeadingLevel(paragraph) === null) continue

    const { text, sectionNumber } = toPlainTextParagraph(paragraph)
    if (!sectionNumber) continue

    headings.push({
      paragraphId: paragraph.uniqueLocalId,
      sectionNumber,
      text: text.trim(),
    })
  }

  return { headings, queued }
}

/** Loads paragraph text and section numbers for plain-text export within a range. */
export async function loadPlainTextParagraphsInRange(
  context: Word.RequestContext,
  range: Word.Range
): Promise<PlainTextParagraph[]> {
  const paragraphs = await loadRangeParagraphs(context, range, 'text, styleBuiltIn')
  return paragraphs.map(toPlainTextParagraph)
}

/** Builds comment-mode anchor entries: syncs listString for headings, prepends numbers, drops empty paragraphs. */
export async function mapAnchorParagraphEntries(
  context: Word.RequestContext,
  paragraphs: Word.Paragraph[]
): Promise<{ id: string; text: string }[]> {
  await syncHeadingListItems(context, paragraphs)

  return paragraphs
    .map((paragraph) => ({
      id: paragraph.uniqueLocalId,
      text: formatPlainTextParagraph(toPlainTextParagraph(paragraph)).trim(),
    }))
    .filter((entry) => entry.text.length > 0)
}

/**
 * Resolves the Word.Range spanning a heading section — from the heading paragraph
 * to just before the next heading of the same or higher level (or end of document).
 * Performs one context.sync internally.
 *
 * Shared by getSectionContentById (returns HTML) and getSectionPlainTextById (returns text)
 * so the boundary-finding logic lives in one place.
 *
 * @throws if the paragraph is not a heading
 */
export async function resolveSectionRange(
  context: Word.RequestContext,
  paragraphId: string
): Promise<Word.Range> {
  // Get the target paragraph directly by its ID
  const targetParagraph = context.document.getParagraphByUniqueLocalId(paragraphId)
  targetParagraph.load('styleBuiltIn')

  // Get the range from the target paragraph to the end of the document
  const endOfDoc = context.document.body.getRange(Word.RangeLocation.end)
  const rangeToEnd = targetParagraph.getRange().expandTo(endOfDoc)

  // Load all paragraphs in this range to find where the section ends
  const paragraphsInRange = rangeToEnd.paragraphs
  paragraphsInRange.load('styleBuiltIn')

  await context.sync()

  // Validate the target is a heading
  const targetLevel = getHeadingLevel(targetParagraph)
  if (targetLevel === null) {
    throw new Error(`Paragraph with ID ${paragraphId} is not a heading`)
  }

  // Find the next heading of same or higher level (section boundary)
  let endParagraph = null
  for (let i = 1; i < paragraphsInRange.items.length; i++) {
    const paragraphLevel = getHeadingLevel(paragraphsInRange.items[i])
    if (paragraphLevel !== null && paragraphLevel <= targetLevel) {
      endParagraph = paragraphsInRange.items[i]
      break
    }
  }

  return endParagraph
    ? targetParagraph.getRange().expandTo(endParagraph.getRange(Word.RangeLocation.start))
    : targetParagraph.getRange().expandTo(endOfDoc)
}

/**
 * Returns the Word.Range of the table containing the given paragraph.
 * Uses the null-object pattern — if the paragraph is not inside a table,
 * getRange() will throw a NullObjectMemberError at the next context.sync(),
 * which callers are expected to catch.
 */
export function resolveTableRange(
  context: Word.RequestContext,
  tableParagraphId: string
): Word.Range {
  const paragraph = context.document.getParagraphByUniqueLocalId(tableParagraphId)
  return paragraph.parentTableOrNullObject.getRange()
}

export const INVALID_TEXT_SECTION_HEADING_ERROR = 'InvalidTextSectionHeadingError'

/**
 * Loads and filters body-only paragraphs for a synthetic text section, performing two syncs.
 * Sync 1: paragraph fields (styleBuiltIn, text).
 * Sync 2: parentTable.isNullObject + any extra loads queued by the caller.
 *
 * @param queueLoads - Called per body paragraph before sync 2. Return value is paired with
 *   the paragraph text in the result. Queue additional Office.js operations here (e.g. getHtml()).
 *   Pass `() => null` when nothing extra is needed.
 * @throws {INVALID_TEXT_SECTION_HEADING_ERROR} if headingId does not resolve to a heading
 * @returns Non-table, non-empty body paragraphs with their plain text and caller data, or [] if empty
 */
export async function resolveTextSectionBodyParagraphs<T>(
  context: Word.RequestContext,
  headingId: string,
  queueLoads: (paragraph: Word.Paragraph) => T
): Promise<{ text: string; data: T }[]> {
  const targetParagraph = context.document.getParagraphByUniqueLocalId(headingId)
  targetParagraph.load('styleBuiltIn')

  const endOfDoc = context.document.body.getRange(Word.RangeLocation.end)
  const paragraphsInRange = targetParagraph.getRange().expandTo(endOfDoc).paragraphs
  paragraphsInRange.load('styleBuiltIn, text')

  await context.sync()

  const targetLevel = getHeadingLevel(targetParagraph)
  if (targetLevel === null) {
    const error = new Error(`Paragraph with ID ${headingId} is not a heading`)
    error.name = INVALID_TEXT_SECTION_HEADING_ERROR
    throw error
  }

  // Stop at the very next heading (any level)
  let boundaryIndex = paragraphsInRange.items.length
  for (let i = 1; i < paragraphsInRange.items.length; i++) {
    if (getHeadingLevel(paragraphsInRange.items[i]) !== null) {
      boundaryIndex = i
      break
    }
  }

  const entries: { text: string; data: T; parentTable: Word.Table }[] = []
  for (let i = 1; i < boundaryIndex; i++) {
    const paragraph = paragraphsInRange.items[i]
    const paragraphText = paragraph.text.trim()
    if (!paragraphText) continue

    const parentTable = paragraph.parentTableOrNullObject
    parentTable.load('isNullObject')
    entries.push({ text: paragraphText, data: queueLoads(paragraph), parentTable })
  }

  if (entries.length === 0) return []

  await context.sync()

  return entries
    .filter(({ parentTable }) => parentTable.isNullObject)
    .map(({ text, data }) => ({ text, data }))
}

/**
 * Every non-empty paragraph in a heading's full section: the heading itself plus every paragraph
 * down to the next heading of the same or higher level. Includes sub-headings, their body text, and
 * table paragraphs (flattened) — table structure is intentionally not preserved here; a dedicated
 * table path handles that. Each paragraph keeps its uniqueLocalId for comment anchoring.
 *
 * Single sync: uniqueLocalId + text + styleBuiltIn are loaded for the whole range at once.
 *
 * @throws {INVALID_TEXT_SECTION_HEADING_ERROR} if headingId does not resolve to a heading
 */
export async function resolveSectionParagraphs(
  context: Word.RequestContext,
  headingId: string
): Promise<{ id: string; text: string }[]> {
  const targetParagraph = context.document.getParagraphByUniqueLocalId(headingId)
  targetParagraph.load('styleBuiltIn')

  const endOfDoc = context.document.body.getRange(Word.RangeLocation.end)
  const paragraphsInRange = targetParagraph.getRange().expandTo(endOfDoc).paragraphs
  paragraphsInRange.load('uniqueLocalId, text, styleBuiltIn')

  await context.sync()

  const targetLevel = getHeadingLevel(targetParagraph)
  if (targetLevel === null) {
    const error = new Error(`Paragraph with ID ${headingId} is not a heading`)
    error.name = INVALID_TEXT_SECTION_HEADING_ERROR
    throw error
  }

  // Section ends at the next heading of the same or higher level, so the whole subtree is included.
  let boundaryIndex = paragraphsInRange.items.length
  for (let i = 1; i < paragraphsInRange.items.length; i++) {
    const level = getHeadingLevel(paragraphsInRange.items[i])
    if (level !== null && level <= targetLevel) {
      boundaryIndex = i
      break
    }
  }

  const sectionParagraphs = paragraphsInRange.items.slice(0, boundaryIndex)
  return mapAnchorParagraphEntries(context, sectionParagraphs)
}
